import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/styles';
import { Bar, Pie } from 'react-chartjs-2';
import moment from 'moment';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  colors,
} from '@material-ui/core';
import Page from '../../components/Page';
import { NetworkContext } from '../../context/NetworkContext';
import Header from './components/Header';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  item: {
    padding: theme.spacing(3),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      '&:not(:last-of-type)': {
        borderRight: `1px solid ${theme.palette.divider}`
      }
    }
  },
  chart: {
    height: 320
  },
  contentCard: {
    height: '100%'
  }
}));

const PIE_COLORS = [
  colors.indigo[500],
  colors.teal[500],
  colors.orange[500],
  colors.purple[400],
  colors.blue[500],
  colors.red[400],
  colors.green[500],
  colors.grey[500],
];

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatMoney(value) {
  return `₹ ${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    xAxes: [{
      gridLines: { display: false, drawBorder: false },
      ticks: { maxTicksLimit: 10 }
    }],
    yAxes: [{
      ticks: { beginAtZero: true, precision: 0 }
    }]
  }
};

export const DashboardAnalytics = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { sendNetworkRequest } = React.useContext(NetworkContext);
  const [startDate, setStartDate] = useState(moment().subtract(29, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  async function fetchAnalytics(start, end) {
    setLoading(true);
    const result = await sendNetworkRequest('/admin/getanalytics', {}, {
      start_date: moment(start).format('YYYY-MM-DD'),
      end_date: moment(end).format('YYYY-MM-DD'),
    }, false);
    setPayload(result);
    setLoading(false);
  }

  useEffect(() => {
    fetchAnalytics(startDate, endDate);
  }, []);

  function handleRangeChange(start, end) {
    setStartDate(start);
    setEndDate(end);
    fetchAnalytics(start, end);
  }

  const totals = payload?.totals || {};
  const usersByDay = payload?.users_by_day || [];
  const platforms = payload?.platforms || [];
  const userTypes = payload?.user_types || [];
  const recentUsers = payload?.recent_users || [];

  const userChart = {
    labels: usersByDay.map(row => moment(row.day).format('DD MMM')),
    datasets: [{
      label: 'New users',
      backgroundColor: theme.palette.primary.main,
      data: usersByDay.map(row => row.count),
    }]
  };

  const platformChart = {
    labels: platforms.map(row => row.label),
    datasets: [{
      data: platforms.map(row => row.count),
      backgroundColor: platforms.map((_, index) => PIE_COLORS[index % PIE_COLORS.length]),
      borderWidth: 0,
    }]
  };

  return (
    <Page
      className={classes.root}
      title="Analytics"
    >
      <Header
        title="Analytics"
        startDate={startDate}
        endDate={endDate}
        onRangeChange={handleRangeChange}
      />
      {loading && <LinearProgress />}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <Grid container>
              <Grid className={classes.item} item md={3} sm={6} xs={12}>
                <Typography gutterBottom variant="overline">Total users</Typography>
                <Typography variant="h3">{formatNumber(totals.users)}</Typography>
              </Grid>
              <Grid className={classes.item} item md={3} sm={6} xs={12}>
                <Typography gutterBottom variant="overline">New users</Typography>
                <Typography variant="h3">{formatNumber(totals.new_users)}</Typography>
              </Grid>
              <Grid className={classes.item} item md={3} sm={6} xs={12}>
                <Typography gutterBottom variant="overline">Active memberships</Typography>
                <Typography variant="h3">{formatNumber(totals.active_memberships)}</Typography>
              </Grid>
              <Grid className={classes.item} item md={3} sm={6} xs={12}>
                <Typography gutterBottom variant="overline">Revenue</Typography>
                <Typography variant="h3">{formatMoney(totals.revenue)}</Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item md={8} xs={12}>
          <Card className={classes.contentCard}>
            <CardHeader title="New users" subheader="By day in the selected range" />
            <Divider />
            <CardContent>
              <div className={classes.chart}>
                <Bar data={userChart} options={barOptions} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item md={4} xs={12}>
          <Card className={classes.contentCard}>
            <CardHeader title="Users by platform" />
            <Divider />
            <CardContent>
              <div className={classes.chart}>
                {platforms.length ? <Pie data={platformChart} options={{ maintainAspectRatio: false, legend: { position: 'bottom' } }} /> : (
                  <Typography color="textSecondary">No platform data</Typography>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {[
              ['Active logins', totals.active_users],
              ['Payments', totals.payments],
              ['Visitors', totals.visitors],
              ['Stall bookings', totals.stalls],
              ['News', totals.news],
              ['Spot', totals.spots],
              ['Futures', totals.updates],
              ['Ads', totals.ads],
            ].map(([label, value]) => (
              <Grid item key={label} md={3} sm={6} xs={12}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="overline">{label}</Typography>
                    <Typography variant="h4">{formatNumber(value)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item md={6} xs={12}>
          <Card className={classes.contentCard}>
            <CardHeader title="Users by type" />
            <Divider />
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Users</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userTypes.length ? userTypes.map(row => (
                  <TableRow key={row.label}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell align="right">{formatNumber(row.count)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={2}>No data</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </Grid>
        <Grid item md={6} xs={12}>
          <Card className={classes.contentCard}>
            <CardHeader title="Recent users" />
            <Divider />
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Joined</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentUsers.length ? recentUsers.map(user => (
                  <TableRow
                    hover
                    key={user.id}
                    component={RouterLink}
                    to={`/edituser/${user.id}/view`}
                    style={{ textDecoration: 'none' }}
                  >
                    <TableCell>{user.name || '-'}</TableCell>
                    <TableCell>{user.mobile || '-'}</TableCell>
                    <TableCell>{user.platform || '-'}</TableCell>
                    <TableCell>{user.createdAt ? moment(user.createdAt).format('DD MMM YYYY') : '-'}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={4}>No users</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </Grid>
      </Grid>
    </Page>
  );
};

export default DashboardAnalytics;
